<!--
# -----------------------------------------------------------------------------
# Component: PolicyItemUOM.vue
# Author: PST
# Created Date: 02/April/2019
# Comments: Policy Coverage -> Risk Item -> UOM
# -----------------------------------------------------------------------------
-->
<template>
  <v-layout row wrap>
    <v-flex xs12>
      <v-toolbar dense color="grey lighten-3" flat>
        <v-spacer></v-spacer>
        <v-btn color="primary" dark class="mb-2" @click="doAddUOM" :disabled="disableItem" v-if="!hideCRUD">
          New UOM Item
          <v-icon right>mdi-plus</v-icon>
        </v-btn>
      </v-toolbar>
      <v-data-table :headers="displayHeaders" :items="itemList" class="elevation-1" :loading="loading">
        <template v-slot:item="row">
          <tr>
            <td>{{ row.item.seqNo }}</td>
            <td>{{ row.item.uom }}</td>
            <td>{{ row.item.uomShortDesc }}</td>
            <td>{{ row.item.cedUomval }}</td>
            <td>{{ row.item.uomVal }}</td>
            <td>{{ row.item.uomUn }}</td>
            <td class="table-action-header" v-if="!hideCRUD">
              <table-action
                :rowData="row"
                review
                @review="doSelectUOM(row.item)"
                :remove="enableRemoveBtn"
                @remove="doPromptRemoveUOM(row.item)"
              ></table-action>
            </td>
          </tr>
        </template>
      </v-data-table>
      <confirm v-model="promptRemoveUOM" @ok="doRemoveUOM">{{ deleteUOM }}</confirm>
    </v-flex>
    <v-flex xs12 v-if="enableUOM && showUOM">
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-pencil</v-icon>
          <v-toolbar-title class="toolbar-title-3">UOM Details </v-toolbar-title>
          <v-spacer></v-spacer>
          <!-- <v-btn color="primary" dark class="mb-2" @click="doSubItem">
            Sub-Item
            <v-icon right>mdi-plus</v-icon>
          </v-btn> -->
          <v-tooltip top v-if="perSubItem">
            <template v-slot:activator="{ on }">
              <v-btn class="mx-2" fab dark small color="purple" v-on="on" @click.native="doSubItem">
                <v-icon dark>mdi-note-text</v-icon>
              </v-btn>
            </template>
            <span>Sub-Item</span>
          </v-tooltip>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-lg>
            <v-layout row wrap>
              <v-flex lg4 xs12>
                <v-autocomplete
                  :items="uomList"
                  v-model="mainForm.uom"
                  :label="labels.uom"
                  item-text="label"
                  item-value="value"
                  data-vv-name="uom"
                  :data-vv-as="labels.uom"
                  :error-messages="errors.collect('uom')"
                  :loading="loading"
                  clearable
                  v-validate="valUOM"
                ></v-autocomplete>
              </v-flex>
              <v-flex lg2 xs12>
                <v-text-field
                  v-model="mainForm.cedUomval"
                  :label="labels.cedUomval"
                  :error-messages="errors.collect('cedUomval')"
                  data-vv-name="cedUomval"
                  :data-vv-as="labels.cedUomval"
                  v-num-mask="{ negative: false, intScale: 18, decScale: 2 }"
                  v-validate="valCedUomval"
                  @change="calcUomVal"
                  :disabled="disableCedUomval"
                ></v-text-field>
              </v-flex>
              <v-flex lg2 xs12>
                <v-text-field
                  v-model="mainForm.uomVal"
                  :label="labels.uomVal"
                  :error-messages="errors.collect('uomVal')"
                  data-vv-name="uomVal"
                  :data-vv-as="labels.uomVal"
                  v-num-mask="{ negative: false, intScale: 18, decScale: 2 }"
                  v-validate="valUOMVal"
                  :disabled="disableUomval"
                ></v-text-field>
              </v-flex>
              <v-flex lg2 xs12>
                <v-autocomplete
                  :items="unList"
                  v-model="mainForm.uomUn"
                  :label="labels.uomUn"
                  item-text="label"
                  item-value="value"
                  data-vv-name="uomUn"
                  :data-vv-as="labels.uomUn"
                  :error-messages="errors.collect('uomUn')"
                  :loading="loading"
                  clearable
                  v-validate="valUN"
                ></v-autocomplete>
              </v-flex>
              <v-flex lg1 xs12>
                <v-text-field
                  v-model="mainForm.uomUplftPct"
                  :label="labels.uomUplftPct"
                  :error-messages="errors.collect('uomUplftPct')"
                  data-vv-name="uomUplftPct"
                  :data-vv-as="labels.uomUplftPct"
                  v-num-mask="{ negative: false, intScale: 18, decScale: 4 }"
                ></v-text-field>
              </v-flex>
              <v-flex lg1 xs12>
                <v-text-field
                  v-model="mainForm.tolerancePct"
                  :label="labels.tolerancePct"
                  :error-messages="errors.collect('tolerancePct')"
                  data-vv-name="tolerancePct"
                  :data-vv-as="labels.tolerancePct"
                  v-num-mask="{ negative: false, intScale: 18, decScale: 4 }"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn depressed @click.prevent="close" :loading="policy.loading"><span class="btn-normal-error">Close</span></v-btn>
                  <v-btn depressed type="submit" @click.prevent="doSaveUOM" :loading="policy.loading"
                    ><span class="btn-normal-primary">{{ labels.save }} UOM</span></v-btn
                  >
                  <warning v-model="warnSaveUOM" @ok="doAcceptSaveUOM">{{ warnSaveUOMMsg }}</warning>
                </v-card-actions>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-flex>
    <v-dialog v-model="subItemDialog" width="60%" persistent>
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon>mdi-plus</v-icon>
          <v-toolbar-title>Create New Sub Item </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text icon @click="doCloseSubItem">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <policy-sub-item ref="polSubItem" @close="doCloseSubItem" @doSubItemSave="subItemDoSaveUOM"></policy-sub-item>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" width="800px">
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon>mdi-information</v-icon>
          <v-toolbar-title>System Information</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <div v-if="messages.length > 0" class="pb-3">
            <span class="info-dialog-msg-header">Oops something went wrong...</span>
            <v-spacer class="mt-2"></v-spacer>
            <ul class="showErrorMsgs">
              <li v-for="(error, index) in messages" :key="index">
                {{ error.message }}
              </li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="showErrorDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showUOMHelpList" width="900px" persistent>
      <UOMHelpList
        v-if="showUOMHelpList"
        :bPackage="bPackage"
        :polItem="polItem"
        @close="showUOMHelpList = false"
        @assign-uom="doAssign($event)"
      ></UOMHelpList>
    </v-dialog>
  </v-layout>
</template>

<script>
import { expandURI } from '@/utils/ajax'
import { uomUN } from '@/const/underwriting'
import MessageMixin from '@/mixins/message'
import { formatPrice } from '@/utils/index'
import { removeFormatNumber } from '@/utils/number'
import { POLITEM_SET_DATA, POLITEMUOM_SET_DATA } from '@/store/types'
import PolicySubItem from './PolicySubItem'
import * as TableConst from '@/mixins/table'
import UOMHelpList from './helplist/UOMHelpList'
import { isEqual } from '@/utils/compare'
import clone from 'clone'

export default {
  data() {
    return {
      subItemDialog: false,
      labels: {
        uomTitle: 'Sum Insured / Limits',
        delUOM: 'Delete UOM',
        editUOM: 'Edit UOM',
        addUOM: 'Add UOM',
        uom: 'UOM',
        cedUomval: 'Cedant SI',
        uomVal: 'SI/Limit',
        uomUplftPct: 'SI Uplift %',
        tolerancePct: 'Tolerance %',
        uomUn: 'UN',
        save: 'Save',
        reset: 'Reset',
        subItem: 'Sub-item',
        tableTitle: 'UOM Summary'
      },
      headers: [
        { text: 'S/N', value: 'seqNo' },
        { text: 'UOM', value: 'uom' },
        { text: 'Description', value: 'uomShortDesc' },
        { text: 'Cedant SI', value: 'cedUomVal' },
        { text: 'SI/Limit', value: 'uomVal' },
        { text: 'UN', value: 'uomUN' },
        { text: 'Action', value: 'action', align: 'center' }
      ],
      headersWOAction: [
        { text: 'S/N', value: 'seqNo' },
        { text: 'UOM', value: 'uom' },
        { text: 'Description', value: 'uomShortDesc' },
        { text: 'Cedant SI', value: 'cedUomVal' },
        { text: 'SI/Limit', value: 'uomVal' },
        { text: 'UN', value: 'uomUN' }
      ],
      itemList: [],
      loading: false,
      policy: {
        loading: false
      },
      selectedUOM: undefined,
      deleteUOM: undefined,
      promptRemoveUOM: undefined,
      uomList: [],
      mainForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        itemNo: undefined,
        seqNo: undefined,
        uom: undefined,
        uomShortDesc: undefined,
        cedUomval: undefined,
        uomVal: undefined,
        uomUn: undefined,
        uomUplftPct: undefined,
        tolerancePct: undefined,
        uomPct: undefined,
        acceptSubItemLimit: false,
        emlPct: undefined,
        uomDesc: undefined
      },
      cloneMainForm: {},
      resetForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        itemNo: undefined,
        seqNo: undefined,
        uom: undefined,
        uomShortDesc: undefined,
        cedUomval: undefined,
        uomVal: undefined,
        uomUn: undefined,
        uomUplftPct: undefined,
        tolerancePct: undefined,
        uomPct: undefined,
        acceptSubItemLimit: false,
        uomDesc: undefined
      },
      showUOM: false,
      protSI: undefined,
      warnSaveUOM: false,
      warnSaveUOMMsg: undefined,
      showErrorDialog: false,
      perSubItem: false,
      showUOMHelpList: false,
      busClsUOMErr: false
    }
  },
  props: {
    polItem: Object,
    disableItem: Boolean,
    isPkgScheme: Boolean,
    hideCRUD: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    displayErrorDialog: {
      handler: function(messages) {
        if (messages && messages.length > 0) {
          this.showErrorDialog = true
        }
      },
      deep: true
    },
    'mainForm.uom': function(value) {
      this.getBusClsUOM(value)
    }
  },
  computed: {
    polMaster() {
      return this.$store.state.underwriting.policy.polMaster
    },
    selectedRiskGrpNo() {
      return this.$store.state.underwriting.policy.selectedRiskGrpNo
    },
    selectedRiskNo() {
      return this.$store.state.underwriting.policy.selectedRiskNo
    },
    selectedPlanNo() {
      return this.$store.state.underwriting.policy.selectedPlanNo
    },
    enableUOM() {
      return this.mainForm.seqNo !== undefined
    },
    unList() {
      let result = []
      Object.keys(uomUN).forEach(function(key) {
        const status = uomUN[key]
        result.push({
          label: status,
          value: key
        })
      })
      return result
    },
    valUOM() {
      if (this.mainForm.uom === undefined || this.mainForm.uom === null || this.mainForm.uom === '') {
        return 'required'
      } else {
        if (this.busClsUOMErr) {
          return 'valUOMField:' + 'This UOM code is invalid or is not permitted for this Business Class. (10087)'
        }
      }
      return ''
    },
    valUOMVal() {
      if (this.polMaster.dri === true) {
        if (
          (this.mainForm.uom === undefined || this.mainForm.uom === null || this.mainForm.uom === '') &&
          parseFloat(removeFormatNumber(this.mainForm.uomVal)) > 0
        ) {
          return 'valUOMField:' + 'Must be zero if UOM code is blank.(10088)'
        } else {
          return ''
        }
      }
      return ''
    },
    valUN() {
      if (
        (this.mainForm.uomUn !== undefined || this.mainForm.uomUn !== null || this.mainForm.uomUn !== '') &&
        parseFloat(removeFormatNumber(this.mainForm.uomVal)) > 0
      ) {
        return 'valUOMField:' + 'Cannot be A%2C B%2C C%2C N or U if value not zero.(10092)'
      } else {
        return ''
      }
    },
    getFirstUOM() {
      return this.itemList.filter(item => {
        return item.seqNo === this.itemList[0].seqNo
      })
    },
    getNewUOM() {
      return this.itemList.filter(item => {
        return item.newUOM === true
      })
    },
    disableCedUomval() {
      return this.polMaster.dri
    },
    valCedUomval() {
      if (this.polMaster.dri === false) {
        if (
          (this.mainForm.uom === undefined || this.mainForm.uom === null || this.mainForm.uom === '') &&
          parseFloat(removeFormatNumber(this.mainForm.cedUomval)) > 0
        ) {
          return 'valUOMField:' + 'Must be zero if UOM code is blank.(10088)'
        } else {
          return ''
        }
      }
      return ''
    },
    enableRemoveBtn() {
      // Enable only if
      // i. [polMaster.scheme] not blank, [xScheme.isPkgScheme] is Yes and [xScheme.multiRiskBen] is “B”, or
      // ii. [polMaster.scheme] is not blank and [xScheme.isPkgScheme] is NO
      // iii. [polMaster.scheme] is blank

      // Protected If [PolMaster.scheme] is not blank [xScheme.isPkgScheme] is Yes (read [xScheme] using
      // [xScheme.scheme] = PolMaster.scheme])
      return !this.bPackage // !(this.polMaster.scheme !== '' && this.isPkgScheme === true)
    },
    bPackage() {
      return this.polMaster.scheme !== '' && this.isPkgScheme === true
    },
    disableUomval() {
      if (this.bPackage) {
        // bPackage is True
        // If [xPkgRiskItem.protSI] is True; protect the field; Otherwise allow user to update
        if (this.protSI) {
          return true
        } else {
          return false
        }
      } else {
        // Otherwise do the followings:
        // • Enable if [polMaster.dri] = TRUE (i.e. direct policy); otherwise protected.
        return !this.polMaster.dri
      }
    },
    displayErrorDialog() {
      return this.messages
    },
    getSelectedUOM() {
      return this.itemList.filter(item => {
        return item.seqNo === this.mainForm.seqNo
      })
    },
    displayHeaders() {
      if (this.hideCRUD) {
        return this.headersWOAction
      } else {
        return this.headers
      }
    }
  },
  methods: {
    async doInit() {
      this.getPolItemUOMParams().then(() => {
        this.getPolItemUOMList()
      })
      this.showUOM = false
    },
    async getPolItemUOMParams() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.polItemUOMParams
      if (apiUrl) {
        try {
          this.loading = true
          const expand = {
            busCls: this.polItem.busCls,
            bPackage: this.bPackage,
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.polItem.riskGrpNo,
            riskNo: this.polItem.riskNo,
            itemNo: this.polItem.itemNo,
            scheme: this.polMaster.scheme
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          if (result.data) {
            this.$set(this, 'uomList', result.data.uomList)
            this.protSI = result.data.protSI
            this.perSubItem = result.data.perSubItem
          }
          this.loading = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doAssign(data) {
      this.mainForm.uom = data.uom
      this.mainForm.uomDesc = data.uom + ' - ' + data.uomLongDesc
    },
    async getPolItemUOMList() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.polItemUOMList
      if (apiUrl) {
        try {
          this.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.polItem.riskGrpNo,
            riskNo: this.polItem.riskNo,
            itemNo: this.polItem.itemNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          this.displayList(result).then(() => {
            this.loading = false
          })
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async displayList(result) {
      const { data } = result
      if (data) {
        this.itemList = []
        data.data.map(item => {
          const convertItem = {
            polNo: item.polNo,
            renCnt: item.renCnt,
            endtCnt: item.endtCnt,
            riskGrpNo: item.riskGrpNo,
            riskNo: item.riskNo,
            itemNo: item.itemNo,
            seqNo: item.seqNo,
            uom: item.uom,
            uomShortDesc: item.uomShortDesc,
            cedUomval: formatPrice(item.cedUomval),
            uomVal: formatPrice(item.uomVal),
            uomUn: item.uomUn,
            uomUplftPct: item.uomUplftPct.toFixed(4),
            tolerancePct: item.tolerancePct.toFixed(4),
            uomPct: item.uomPct,
            newUOM: item.newUOM
          }
          this.itemList.push(convertItem)
        })
      } else {
        this.itemList = []
      }
      return true
    },
    doSelectUOM(data) {
      this.getPolItemUOMParams().then(() => {
        this.$emit('hidePanel', 'IUOM')
        this.setUOMDetails(data)
        this.showUOM = true
      })
    },
    setUOMDetails(data) {
      this.mainForm.polNo = data.polNo
      this.mainForm.renCnt = data.renCnt
      this.mainForm.endtCnt = data.endtCnt
      this.mainForm.riskGrpNo = data.riskGrpNo
      this.mainForm.riskNo = data.riskNo
      this.mainForm.itemNo = data.itemNo
      this.mainForm.seqNo = data.seqNo
      this.mainForm.uom = data.uom
      this.mainForm.uomShortDesc = data.uomShortDesc
      if (this.mainForm.uom !== undefined) {
        this.mainForm.uomDesc = this.mainForm.uom + ' - ' + this.mainForm.uomShortDesc
      } else {
        this.mainForm.uomDesc = ''
      }
      this.mainForm.cedUomval = data.cedUomval
      this.mainForm.uomVal = data.uomVal
      this.mainForm.uomUn = data.uomUn
      this.mainForm.uomUplftPct = data.uomUplftPct
      this.mainForm.tolerancePct = data.tolerancePct
      this.mainForm.uomPct = data.uomPct
      this.mainForm.acceptSubItemLimit = false

      this.resetForm = clone(this.mainForm)
      this.cloneMainForm = clone(this.mainForm)
    },
    async doPromptRemoveUOM(data) {
      if (data.seqNo === 1) {
        this.$toast.warning('Not permitted to delete first UOM')
      } else {
        this.selectedUOM = data
        this.deleteUOM = 'Are you sure you want to delete UOM ' + data.uom + '?'
        this.promptRemoveUOM = true
      }
    },
    async doRemoveUOM() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.delUOM
      if (apiUrl) {
        try {
          this.policy.loading = true
          const expand = {
            polNo: this.selectedUOM.polNo,
            renCnt: this.selectedUOM.renCnt,
            endtCnt: this.selectedUOM.endtCnt,
            riskGrpNo: this.selectedUOM.riskGrpNo,
            riskNo: this.selectedUOM.riskNo,
            itemNo: this.selectedUOM.itemNo,
            seqNo: this.selectedUOM.seqNo
          }

          const result = await this.$http.get(expandURI(apiUrl, expand))

          this.displayList(result).then(() => {
            this.policy.loading = false
          })
          this.$toast.success('UOM record successfully deleted.')
          // lyt
          this.mainForm.seqNo = undefined
          this.showUOM = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async doAddUOM() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.addUOM
      if (apiUrl) {
        try {
          this.policy.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.polItem.riskGrpNo,
            riskNo: this.polItem.riskNo,
            itemNo: this.polItem.itemNo
          }

          const result = await this.$http.get(expandURI(apiUrl, expand))

          this.displayList(result).then(() => {
            this.policy.loading = false
            this.doSelectUOM(this.getNewUOM[0])
          })
          this.$toast.success('New UOM item successfully saved.')
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async subItemDoSaveUOM() {
      let output = 'ERROR'
      this.resetMessages()
      let result = await this.$validator.validateAll()
      if (result) {
        this.policy.loading = true
        this.$parent.$parent.$parent.$parent.$parent.$parent.$parent.policy.saving = true
        let success = await this.doPolItemUOMSave()
        if (success) {
          output = 'SUCCESS'
          // let tempForm = clone(this.mainForm)
          this.doInitForm()
          this.getPolItemUOMList()
          this.$emit('refreshItemDetails')
          this.$toast.success('UOM item successfully saved.')
          this.$emit('hidePanel', 'RESET')
        }
        this.policy.loading = false
        this.$parent.$parent.$parent.$parent.$parent.$parent.$parent.policy.saving = false
      } else {
        this.promptFieldCheck()
      }
      return output
    },
    async doSaveUOM() {
      let output = 'ERROR'
      if (isEqual(this.cloneMainForm, this.mainForm)) {
        this.close()
        return 'NOCHANGE'
      }

      this.resetMessages()
      let result = await this.$validator.validateAll()
      if (result) {
        this.policy.loading = true
        this.$parent.$parent.$parent.$parent.$parent.$parent.$parent.policy.saving = true
        let success = await this.doPolItemUOMSave()
        if (success) {
          output = 'SUCCESS'
          // let tempForm = clone(this.mainForm)
          this.doInitForm()
          this.getPolItemUOMList()
          // this.$emit('itemList') // 2020-09-28 LYT OS-474
          // this.$emit('refreshItemDetails', tempForm) // AR: OS-88 Fix
          this.$emit('refreshItemDetails')
          this.$toast.success('UOM item successfully saved.')
          this.$emit('hidePanel', 'RESET')
        }
        this.policy.loading = false
        this.$parent.$parent.$parent.$parent.$parent.$parent.$parent.policy.saving = false
      } else {
        this.promptFieldCheck()
      }
      return output
    },
    async doPolItemUOMSave() {
      try {
        // this.getEmlAmt()
        this.mainForm.emlPct = this.polItem.emlPct
        this.mainForm.cedUomval = removeFormatNumber(this.mainForm.cedUomval)
        this.mainForm.uomVal = removeFormatNumber(this.mainForm.uomVal)
        const result = await this.$store.dispatch('savePolItemUOM', this.mainForm)
        if (result) {
          return true
        } else {
          if (this.$store.state.underwriting.policy.polItemUOM.saveError.types[0] === 'WARN') {
            this.warnSaveUOMMsg = this.$store.state.underwriting.policy.polItemUOM.saveError.messages[0]
            this.warnSaveUOM = true
          } else if (this.$store.state.underwriting.policy.polItemUOM.saveError.types[0] === 'ERROR') {
            this.messages.push({
              message: this.$store.state.underwriting.policy.polItemUOM.saveError.messages[0]
            })
          }
          return false
        }
      } catch (err) {
        this.$toast.error('An unexpected error occured while saving UOM record.')
        return false
      }
    },
    async getBusClsUOM(uom) {
      this.resetMessages()
      this.loading = true
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.busClsUOM
      if (apiUrl) {
        const expand = {
          busCls: this.polItem.busCls,
          uom: uom
        }
        this.$http
          .get(expandURI(apiUrl, expand))
          .then(result => {
            const { data } = result.data
            if (data) {
              this.busClsUOMErr = false
            } else {
              this.busClsUOMErr = true
            }
            this.loading = false
          })
          .catch(err => {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
            this.busClsUOMErr = true
            this.loading = false
          })
      }
    },
    doResetUOM() {
      this.mainForm = clone(this.resetForm)
      this.cloneMainForm = clone(this.resetForm)
    },
    doInitForm() {
      this.mainForm.polNo = undefined
      this.mainForm.renCnt = undefined
      this.mainForm.endtCnt = undefined
      this.mainForm.riskGrpNo = undefined
      this.mainForm.riskNo = undefined
      this.mainForm.itemNo = undefined
      this.mainForm.seqNo = undefined
      this.mainForm.uom = undefined
      this.mainForm.cedUomval = undefined
      this.mainForm.uomVal = undefined
      this.mainForm.uomUn = undefined
      this.mainForm.uomUplftPct = undefined
      this.mainForm.tolerancePct = undefined
      this.mainForm.uomPct = undefined

      this.resetForm = clone(this.mainForm)
      this.cloneMainForm = clone(this.mainForm)
    },
    doSubItem() {
      this.$store.commit(POLITEM_SET_DATA, this.polItem)
      this.$store.commit(POLITEMUOM_SET_DATA, this.mainForm)
      this.subItemDialog = true
      // 2021-04-12 LYT OS-748
      if (this.$refs) {
        if (this.$refs.polSubItem) this.$refs.polSubItem.doInit()
      }
      // this.$router.push({ name: 'policySubItem' })
    },
    calcUomVal() {
      var cedUomval = removeFormatNumber(this.mainForm.cedUomval)
      this.mainForm.uomVal = formatPrice((cedUomval * this.polMaster.ourSharePct) / 100)
    },
    doAcceptSaveUOM() {
      this.mainForm.acceptSubItemLimit = true
      this.doSaveUOM()
    },
    async doCloseSubItem() {
      this.subItemDialog = false
      // this.$emit('itemList') // 2020-09-28 LYT OS-474
      // await this.getPolItemUOMList()
      // this.doSelectUOM(this.getSelectedUOM[0])
      // await this.$emit('initCoverage')
      await this.getPolItemUOMList()
      this.doSelectUOM(this.getSelectedUOM[0])
    },
    close() {
      this.showUOM = false
      this.$emit('hidePanel', 'RESET')
    }
  },
  beforeMount() {
    if (this.$store.state.underwriting.policyRiskItemSettings.init === 1) {
      // this.doInit()
    }
  },
  created() {
    // Local validator
    const valUOMField = {
      getMessage: (field, args) => {
        if (args === void 0) args = []
        var errMsg = args[0]
        errMsg = errMsg.replace(/%2C/g, ',')
        return errMsg
      },
      validate: (value, args) => {
        if (value) {
          if (args === void 0) args = []
          var errMsg = args[0]
          if (errMsg) {
            return false
          } else return true
        } else return true
      }
    }
    this.$validator.extend('valUOMField', valUOMField)
  },
  components: {
    PolicySubItem,
    UOMHelpList
  },
  mixins: [TableConst.default, MessageMixin]
}
</script>

<style></style>
